import type { JSX, ReactNode } from 'react';
import { StatusBadge } from '@/components/dashboard/StatusBadge';
import { WebsiteLink } from './WebsiteLink';
import { categoryLabel, orDash, type TenantProfile } from './tenantProfileData';

interface TenantProfilePreviewProps {
  tenant: TenantProfile;
}

/** Read-only expanded view of a single tenant, rendered inside the dashboard drawer. */
export function TenantProfilePreview({ tenant }: TenantProfilePreviewProps): JSX.Element {
  const email = tenant.email?.trim();
  const phone = tenant.phone?.trim();

  const details: Array<{ label: string; value: ReactNode }> = [
    { label: 'Suite', value: orDash(tenant.suite) },
    { label: 'Floor', value: orDash(tenant.floor) },
    { label: 'Hours', value: orDash(tenant.hours) },
    { label: 'Established', value: orDash(tenant.established) },
    { label: 'Website', value: <WebsiteLink website={tenant.website} /> },
  ];

  if (email != null && email.length > 0) {
    details.push({
      label: 'Email',
      value: (
        <a href={`mailto:${email}`} className="text-brand-primary hover:underline">
          {email}
        </a>
      ),
    });
  }

  if (phone != null && phone.length > 0) {
    details.push({
      label: 'Phone',
      value: (
        <a href={`tel:${phone}`} className="text-brand-primary hover:underline">
          {phone}
        </a>
      ),
    });
  }

  return (
    <div className="flex flex-col gap-lg">
      {tenant.hero != null ? (
        <figure className="overflow-hidden rounded-2xl border border-surface-subtle bg-surface-subtle/40">
          <img
            src={tenant.hero.src}
            alt={tenant.hero.alt}
            loading="lazy"
            className="h-44 w-full"
            style={{
              objectFit: tenant.hero.fit ?? 'cover',
              objectPosition: tenant.hero.position ?? 'center',
            }}
          />
          {tenant.hero.credit != null ? (
            <figcaption className="px-3 py-1.5 text-[0.7rem] text-text-primary/50">
              {tenant.hero.credit}
            </figcaption>
          ) : null}
        </figure>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        <StatusBadge label={categoryLabel(tenant.categoryId)} tone="neutral" />
        {tenant.featured ? <StatusBadge label="Featured" tone="info" /> : null}
        {tenant.appointmentOnly ? <StatusBadge label="By appointment" tone="warning" /> : null}
      </div>

      <section className="rounded-2xl border border-surface-subtle bg-white p-5 shadow-sm">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-text-primary/60">
          Practical details
        </h3>
        <dl className="mt-3">
          {details.map((detail) => (
            <div
              key={detail.label}
              className="flex flex-col gap-0.5 border-b border-surface-subtle py-2 last:border-b-0 sm:flex-row sm:items-baseline sm:gap-3"
            >
              <dt className="text-xs font-semibold uppercase tracking-wide text-text-primary/50 sm:w-32 sm:flex-shrink-0">
                {detail.label}
              </dt>
              <dd className="text-sm text-text-primary">{detail.value}</dd>
            </div>
          ))}
        </dl>
      </section>

      {tenant.story.length > 0 ? (
        <section className="flex flex-col gap-2">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-text-primary/60">
            Story
          </h3>
          <div className="flex flex-col gap-3 text-sm leading-relaxed text-text-primary/80">
            {tenant.story.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
          </div>
        </section>
      ) : null}

      {tenant.specialties.length > 0 ? (
        <section className="flex flex-col gap-2">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-text-primary/60">
            Specialties
          </h3>
          <ul className="flex flex-wrap gap-2">
            {tenant.specialties.map((specialty) => (
              <li
                key={specialty}
                className="rounded-full border border-surface-subtle bg-surface-subtle/50 px-3 py-1 text-xs font-medium text-text-primary/80"
              >
                {specialty}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}
